import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, Foot, field, lbl } from './dialog';
import { errBox, note } from './ui';

// ── Why a posted entry is being reversed (batch 11 R2, design §3.5.2) ───────────────────────────────
// A reversal is the only correction a posted entry can receive, and the Journal route refuses one with no
// reason (400 reason_required): the reason is kept on the reversal (journal_entries.reason) and printed after
// its explanation in calc_note, the voucher's explanation column that DBD ข้อ 4 asks for. Both screens that
// reverse — Manual entries' picker (Bookkeeping/ManualEntry.tsx) and Fixed assets › Depreciation's "Reverse &
// recalculate" (panes/fixedAssets/DepreciationTab.tsx) — ask through THIS one dialog, in the same commit as the
// route, so neither can send a reversal the route will certainly refuse.
//
// ⛔ The request stays in the CALLER (onSubmit): Manual entries raises a draft, Depreciation raises it and posts
//    it, and testEntryDetail pins `api.jnlReverse(` to the screens that own the action. This file writes nothing.
// ⛔ Submit is held while the trimmed reason is empty or the call is in flight. Typed text makes every exit ask first
//    (FS-4).
// ⛔ A REFUSAL (HTTP 400–499) is never offered a second press (W1 review, screen lens): every one the route gives is
//    decided by the books — already_reversed (another tab got there first), asset, asset_reversal, reversal_of_released,
//    no_open_period — so the same press can only be refused again, and every refused POST pages the admins. The
//    dialog hands the server's words to the caller's onRefused, which closes it, says them on its own screen and reads
//    its list again (so the row says why in place of Reverse); Submit is held for the rest of the dialog's life either
//    way, nothing typed here can change the books' answer. Only a network failure or a 5xx, where the same press can
//    succeed, is shown here with the dialog left open and Submit free to retry. Depreciation's onSubmit never rejects:
//    it closes on any answer, says a refusal above its list and holds its buttons until that list is read again.

/** The longest reason the route keeps (services/manualEntry.ts REVERSE_REASON_MAX). */
const REASON_MAX = 500;

export function ReverseReasonDialog({ docNo, line, submitLabel, onClose, onSubmit, onRefused }: {
  /** The entry being reversed, beside the title. */
  docNo: string;
  /** What is about to happen, in the caller's words; the generic sentence when none is given. */
  line?: React.ReactNode;
  /** The submit button's label (Depreciation: "Reverse and post"). */
  submitLabel?: string;
  onClose: () => void;
  /** Sends the reversal with the trimmed reason. A network failure or a 5xx is shown here and the dialog stays open. */
  onSubmit: (reason: string) => Promise<unknown>;
  /** The server REFUSED it (HTTP 400–499), in its words: the caller closes the dialog, says them and reads its list
   *  again. Without it the dialog stays open with the words and Submit held, so it can only be closed. */
  onRefused?: (message: string) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  // Set by a refusal: Submit stays held until the dialog is gone.
  const [refused, setRefused] = useState(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const ready = !!reason.trim() && !refused;
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try { await onSubmit(reason.trim()); }
    catch (e: any) {
      const message = String(e?.message || e);
      const status = Number(e?.status) || 0;
      const isRefusal = status >= 400 && status < 500;
      if (mounted.current) { setErr(message); if (isRefusal) setRefused(true); }
      if (isRefusal && onRefused) onRefused(message);
    }
    finally { if (mounted.current) setBusy(false); }
  };
  return (
    <Dialog title={t('aw_rvr_title')} docNo={docNo} onClose={onClose} busy={busy} dirty={dirty} width={480}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-rvr-dialog>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', whiteSpace: 'pre-line', lineHeight: 1.55 }} data-rvr-line>
          {line ?? t('aw_rvr_line', { docNo })}
        </div>
        <div>
          <label style={lbl} htmlFor="rvr-reason">{t('aw_rvr_reason')}</label>
          <textarea id="rvr-reason" value={reason} maxLength={REASON_MAX} rows={3} autoFocus data-rvr-reason
            onChange={e => { setDirty(true); setReason(e.target.value); }} style={{ ...field, resize: 'vertical' }} />
          <div style={note}>{t('aw_rvr_hint')}</div>
        </div>
        {err && <div role="alert" style={errBox} data-rvr-err>{err}</div>}
        <Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-arrow-back-up" label={submitLabel || t('aw_rvr_submit')} t={t} />
      </div>
    </Dialog>
  );
}
